import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import api from '../../services/api';
import styles from './EdgettsManagement.module.css';

const EDGE_TTS_ENDPOINT_PREFIX = '/edge-tts';
const PREVIEW_MAX_LENGTH = 500;

const CATEGORY_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'multilingual', label: '多语言' },
];

function EdgettsManagement() {
  // 声音列表（来自微软 API）
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicesError, setVoicesError] = useState('');
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voiceCategoryFilter, setVoiceCategoryFilter] = useState('');

  // 卷列表
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [volumesError, setVolumesError] = useState('');
  const [selectedVolumeId, setSelectedVolumeId] = useState('');

  const [selectedMode, setSelectedMode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [chaptersError, setChaptersError] = useState('');
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [resultProgressExpanded, setResultProgressExpanded] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState('');
  const [activeTask, setActiveTask] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollingRef = useRef(null);

  // 试听相关状态
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewAudioUrl, setPreviewAudioUrl] = useState('');
  const previewAudioRef = useRef(null);


  // 按分类筛选后的声音列表
  const filteredVoices = useMemo(() => {
    if (!voiceCategoryFilter) {
      return voices;
    }
    return voices.filter((v) => v.category === voiceCategoryFilter);
  }, [voices, voiceCategoryFilter]);

  const computeModeAvailability = (chapter) => {
    if (!chapter) {
      return { en: false, cn: false, podcast: false };
    }
    const hasContent = typeof chapter.content === 'string' && chapter.content.trim().length > 0;
    const hasContentTrans = typeof chapter.content_trans === 'string' && chapter.content_trans.trim().length > 0;
    const hasPodcast = typeof chapter.podcast === 'string' && chapter.podcast.trim().length > 0;

    return {
      en: hasContent,
      cn: hasContentTrans,
      podcast: hasPodcast,
    };
  };

  const resolveDefaultMode = (chapter) => {
    const availability = computeModeAvailability(chapter);
    if (availability.en) {
      return 'en';
    }
    if (availability.cn) {
      return 'cn';
    }
    if (availability.podcast) {
      return 'podcast';
    }
    return '';
  };

  const selectedChapter = useMemo(
    () => chapters.find((chapter) => String(chapter.id) === String(selectedChapterId)),
    [chapters, selectedChapterId],
  );

  useEffect(() => {
    if (!selectedChapter) {
      if (selectedMode) {
        setSelectedMode('');
      }
      return;
    }

    const availability = computeModeAvailability(selectedChapter);
    if (selectedMode && availability[selectedMode]) {
      return;
    }

    const fallback = resolveDefaultMode(selectedChapter);
    if (fallback !== selectedMode) {
      setSelectedMode(fallback);
    }
  }, [selectedChapter, selectedMode]);

  // 清理试听音频 URL
  useEffect(() => {
    return () => {
      if (previewAudioUrl) {
        URL.revokeObjectURL(previewAudioUrl);
      }
    };
  }, [previewAudioUrl]);

  const handlePreview = useCallback(async () => {
    const text = previewText.trim();
    if (!text) {
      setPreviewError('请输入试听文本');
      return;
    }
    if (!selectedVoice) {
      setPreviewError('请先选择声音');
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');

    // 先停止当前播放并释放旧 URL
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    if (previewAudioUrl) {
      URL.revokeObjectURL(previewAudioUrl);
      setPreviewAudioUrl('');
    }

    try {
      const response = await api.post(
        `${EDGE_TTS_ENDPOINT_PREFIX}/tts/preview`,
        { text, voice: selectedVoice.shortName },
        { responseType: 'blob', timeout: 30000 },
      );
      const blob = new Blob([response.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      setPreviewAudioUrl(url);

      // 自动播放
      setTimeout(() => {
        if (previewAudioRef.current) {
          previewAudioRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (err) {
      let detail = '';
      if (err?.response?.data instanceof Blob) {
        try {
          const errText = await err.response.data.text();
          const json = JSON.parse(errText);
          detail = json?.detail || '';
        } catch {
          // 解析失败
        }
      }
      if (!detail) {
        detail = err?.response?.data?.detail || err?.message || '试听失败，请稍后重试';
      }
      setPreviewError(typeof detail === 'string' ? detail : '试听失败，请稍后重试');
    } finally {
      setPreviewLoading(false);
    }
  }, [previewText, selectedVoice, previewAudioUrl]);

  useEffect(() => {
    fetchVoices();
    fetchVolumes();
  }, []);

  // 选中的卷变化时，重新加载该卷的章节
  useEffect(() => {
    if (selectedVolumeId) {
      fetchChapters(selectedVolumeId);
    } else {
      setChapters([]);
      setSelectedChapterId('');
      setSelectedMode('');
    }
  }, [selectedVolumeId]);

  useEffect(() => () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!activeTaskId) {
      return undefined;
    }

    const pollStatus = async () => {
      try {
        const { data } = await api.get(`${EDGE_TTS_ENDPOINT_PREFIX}/tts/${activeTaskId}`);
        setActiveTask(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPolling(false);
          if (data.status === 'completed') {
            setResult(data);
            setActiveTask(null);
          } else {
            setSubmitError(data.error || '生成语音失败，请稍后再试');
          }
          setActiveTaskId('');
        }
      } catch (error) {
        console.error('查询任务进度失败:', error);
      }
    };

    pollStatus();
    if (!pollingRef.current) {
      pollingRef.current = setInterval(pollStatus, 2000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [activeTaskId]);

  const fetchVoices = async () => {
    setVoicesLoading(true);
    setVoicesError('');
    try {
      const { data } = await api.get(`${EDGE_TTS_ENDPOINT_PREFIX}/voices/all`);
      const list = Array.isArray(data?.voices) ? data.voices : [];
      setVoices(list);

      // 保持当前选中，如不存在则选第一个
      setSelectedVoice((prev) => {
        if (prev && list.some((v) => v.shortName === prev.shortName)) {
          return prev;
        }
        return list.length > 0 ? list[0] : null;
      });
    } catch (error) {
      console.error('获取声音列表失败:', error);
      setVoicesError('获取声音列表失败，请稍后重试');
    } finally {
      setVoicesLoading(false);
    }
  };

  const fetchVolumes = async () => {
    setVolumesLoading(true);
    setVolumesError('');
    try {
      const { data } = await api.get('/volumes/', { params: { limit: 500 } });
      const list = Array.isArray(data) ? data : [];
      setVolumes(list);

      setSelectedVolumeId((prev) => {
        if (prev && list.some((v) => String(v.id) === String(prev))) {
          return prev;
        }
        return list.length > 0 ? String(list[0].id) : '';
      });
    } catch (error) {
      console.error('获取卷列表失败:', error);
      setVolumesError('获取卷列表失败，请稍后重试');
    } finally {
      setVolumesLoading(false);
    }
  };

  const fetchChapters = async (volumeId) => {
    setChaptersLoading(true);
    setChaptersError('');
    try {
      const { data } = await api.get('/chapters/', { params: { volume_id: volumeId, limit: 500 } });
      const list = Array.isArray(data) ? data : [];
      setChapters(list);

      if (list.length === 0) {
        setSelectedChapterId('');
        setSelectedMode('');
        return;
      }

      const existing = list.find((chapter) => String(chapter.id) === String(selectedChapterId));
      const target = existing || list[0];
      setSelectedChapterId(String(target.id));
      setSelectedMode(resolveDefaultMode(target));
    } catch (error) {
      console.error('获取章节列表失败:', error);
      setChaptersError('获取章节列表失败，请稍后重试');
    } finally {
      setChaptersLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setResult(null);
    setActiveTask(null);
    setActiveTaskId('');
    setPolling(false);
    setResultProgressExpanded(false);

    if (!selectedVoice) {
      setSubmitError('请选择要使用的声音');
      return;
    }

    if (!selectedChapterId) {
      setSubmitError('请选择章节');
      return;
    }

    if (!selectedMode) {
      setSubmitError('请选择生成类型');
      return;
    }

    const chapter = selectedChapter;
    if (!chapter) {
      setSubmitError('未找到所选章节，请刷新后重试');
      return;
    }

    const availability = computeModeAvailability(chapter);
    if (!availability[selectedMode]) {
      const modeFieldMap = {
        cn: 'content',
        en: 'content_trans',
        podcast: 'podcast',
      };
      setSubmitError(`所选章节缺少 ${modeFieldMap[selectedMode] || selectedMode} 内容`);
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await api.post(
        `${EDGE_TTS_ENDPOINT_PREFIX}/tts/chapter`,
        {
          chapter_id: Number(chapter.id),
          mode: selectedMode,
          voice: selectedVoice.shortName,
        },
      );

      if (data?.task_id) {
        setActiveTaskId(data.task_id);
        setPolling(true);
        setActiveTask({
          status: data.status,
          voice: data.voice,
          chapter_id: data.chapter_id,
          mode: data.mode,
        });
      } else {
        setResult(data);
        setActiveTask(null);
      }
    } catch (error) {
      console.error('生成语音失败:', error);
      const message = error?.response?.data?.detail || '生成语音失败，请稍后再试';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderVoiceOptionLabel = (voice) => {
    const name = voice.localName || voice.displayName || voice.shortName;
    const parts = [name];
    if (voice.gender) {
      parts.push(`[${voice.gender === 'Female' ? '女' : voice.gender === 'Male' ? '男' : voice.gender}]`);
    }
    parts.push(`| ${voice.locale}`);
    if (voice.category === 'multilingual') {
      parts.push('| 多语言');
    }
    return parts.join(' ');
  };


  const selectedChapterAvailability = computeModeAvailability(selectedChapter);
  const chapterContentLengths = {
    en: selectedChapter && typeof selectedChapter.content === 'string' ? selectedChapter.content.length : 0,
    cn: selectedChapter && typeof selectedChapter.content_trans === 'string' ? selectedChapter.content_trans.length : 0,
    podcast: selectedChapter && typeof selectedChapter.podcast === 'string' ? selectedChapter.podcast.length : 0,
  };

  const modeOptions = [
    { value: 'cn', label: '中文（content）' },
    { value: 'en', label: '英文（content_trans）' },
    { value: 'podcast', label: '播客文本（podcast）' },
  ];

  return (
    <div className={styles.edgettsManagement}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.refreshBtn}
          onClick={() => {
            fetchVoices();
            fetchVolumes();
          }}
          disabled={voicesLoading || volumesLoading || chaptersLoading}
        >
          {(voicesLoading || volumesLoading || chaptersLoading) ? '刷新中...' : '刷新数据'}
        </button>
      </div>

      <div className={styles.panel}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="voiceSelect">
              选择声音
              {voices.length > 0 && (
                <span className={styles.voiceCount}>
                  （共 {voices.length} 个，当前显示 {filteredVoices.length} 个）
                </span>
              )}
            </label>

            <div className={styles.voiceFilterRow}>
              {CATEGORY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`${styles.filterChip} ${voiceCategoryFilter === opt.value ? styles.filterChipActive : ''}`}
                  onClick={() => {
                    setVoiceCategoryFilter(opt.value);
                    // 如果当前选中的声音不在筛选结果中，自动选第一个
                    const nextList = opt.value
                      ? voices.filter((v) => v.category === opt.value)
                      : voices;
                    if (selectedVoice && !nextList.some((v) => v.shortName === selectedVoice.shortName)) {
                      setSelectedVoice(nextList.length > 0 ? nextList[0] : null);
                    }
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <select
              id="voiceSelect"
              className={styles.select}
              value={selectedVoice?.shortName || ''}
              onChange={(event) => {
                const voice = filteredVoices.find((v) => v.shortName === event.target.value);
                setSelectedVoice(voice || null);
              }}
              disabled={voicesLoading || filteredVoices.length === 0}
            >
              {filteredVoices.length === 0 && <option value="">暂无可用声音</option>}
              {filteredVoices.map((voice) => (
                <option key={voice.shortName} value={voice.shortName}>
                  {renderVoiceOptionLabel(voice)}
                </option>
              ))}
            </select>
            {voicesError && <div className={styles.error}>{voicesError}</div>}
            {selectedVoice && (
              <div className={styles.hint}>
                API 名称：{selectedVoice.shortName}
                {selectedVoice.styles && selectedVoice.styles.length > 0 && (
                  <> · 风格：{selectedVoice.styles.join(', ')}</>
                )}
              </div>
            )}

            {/* 试听区域 */}
            {selectedVoice && (
              <div className={styles.previewSection}>
                <div className={styles.previewHeader}>
                  <span className={styles.previewLabel}>
                    <span className="material-symbols-outlined">headphones</span>
                    试听音色
                  </span>
                  <span className={styles.previewCharCount}>
                    {previewText.length}/{PREVIEW_MAX_LENGTH}
                  </span>
                </div>
                <div className={styles.previewInputRow}>
                  <textarea
                    className={styles.previewTextarea}
                    value={previewText}
                    onChange={(e) => {
                      if (e.target.value.length <= PREVIEW_MAX_LENGTH) {
                        setPreviewText(e.target.value);
                        setPreviewError('');
                        // 文本变化后，清除旧音频，下次点击重新生成
                        if (previewAudioUrl) {
                          URL.revokeObjectURL(previewAudioUrl);
                          setPreviewAudioUrl('');
                          if (previewAudioRef.current) {
                            previewAudioRef.current.pause();
                          }
                        }
                      }
                    }}
                    placeholder="输入试听文本，例如：你好，欢迎使用语音合成服务。Hello, welcome to TTS."
                    disabled={previewLoading}
                    rows={2}
                  />
                  <button
                    type="button"
                    className={styles.previewBtn}
                    onClick={handlePreview}
                    disabled={previewLoading || !previewText.trim()}
                  >
                    {previewLoading ? (
                      <>生成中…</>
                    ) : (
                      <>
                        <span className="material-symbols-outlined">play_circle</span>
                        试听
                      </>
                    )}
                  </button>
                </div>
                {previewAudioUrl && (
                  <div className={styles.previewAudioRow}>
                    <audio
                      ref={previewAudioRef}
                      src={previewAudioUrl}
                      controls
                      className={styles.previewAudio}
                    />
                  </div>
                )}
                {previewError && (
                  <div className={styles.previewError}>{previewError}</div>
                )}
              </div>
            )}
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="volumeSelect">
              选择卷
            </label>
            <select
              id="volumeSelect"
              className={styles.select}
              value={selectedVolumeId}
              onChange={(event) => {
                setSelectedVolumeId(event.target.value);
                setSubmitError('');
                setResult(null);
              }}
              disabled={volumesLoading || volumes.length === 0}
            >
              {volumes.length === 0 && <option value="">暂无可用卷</option>}
              {volumes.map((volume) => (
                <option key={volume.id} value={volume.id}>
                  {volume.novel?.title ? `${volume.novel.title} / ` : ''}
                  {volume.title}
                </option>
              ))}
            </select>
            {volumesError && <div className={styles.error}>{volumesError}</div>}
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="chapterSelect">
              选择章节
              {chapters.length > 0 && (
                <span className={styles.voiceCount}>（共 {chapters.length} 章）</span>
              )}
            </label>
            <select
              id="chapterSelect"
              className={styles.select}
              value={selectedChapterId}
              onChange={(event) => {
                setSelectedChapterId(event.target.value);
                setSubmitError('');
                setResult(null);
              }}
              disabled={chaptersLoading || chapters.length === 0 || !selectedVolumeId}
            >
              {!selectedVolumeId && <option value="">请先选择卷</option>}
              {selectedVolumeId && chapters.length === 0 && <option value="">{chaptersLoading ? '加载中...' : '该卷暂无章节'}</option>}
              {chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapter.title}
                </option>
              ))}
            </select>
            {chaptersError && <div className={styles.error}>{chaptersError}</div>}
            {selectedChapter && (
              <div className={styles.hint}>
                可用内容：
                英文 JSON：{selectedChapterAvailability.en ? `${chapterContentLengths.en} 字` : '无'}；
                中文 JSON：{selectedChapterAvailability.cn ? `${chapterContentLengths.cn} 字` : '无'}；
                播客文本：{selectedChapterAvailability.podcast ? `${chapterContentLengths.podcast} 字` : '无'}
              </div>
            )}
          </div>

          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="modeSelect">
              生成类型
            </label>
            <select
              id="modeSelect"
              className={styles.select}
              value={selectedMode || ''}
              onChange={(event) => {
                setSelectedMode(event.target.value);
                setSubmitError('');
                setResult(null);
              }}
              disabled={!selectedChapter || chaptersLoading}
            >
              <option value="">请选择生成类型</option>
              {modeOptions.map((option) => {
                const disabled = !selectedChapterAvailability[option.value];
                const optionLabel = `${option.label}${disabled ? '（不可用）' : ''}`;
                return (
                  <option key={option.value} value={option.value} disabled={disabled}>
                    {optionLabel}
                  </option>
                );
              })}
            </select>
            {!selectedChapter && (
              <div className={styles.hint}>请选择章节后再选择类型</div>
            )}
            {selectedChapter && selectedMode && selectedChapterAvailability[selectedMode] && (
              <div className={styles.hint}>
                输出路径：OSS audios/{selectedChapterId}/{selectedMode}.mp3 / {selectedMode}.srt
              </div>
            )}
            {selectedChapter
              && !selectedChapterAvailability.en
              && !selectedChapterAvailability.cn
              && !selectedChapterAvailability.podcast && (
                <div className={styles.error}>该章节暂无可用内容</div>
            )}
          </div>

          {submitError && <div className={styles.error}>{submitError}</div>}

          <div className={styles.actions}>
            <button type="submit" className={styles.submitBtn} disabled={submitting || polling}>
              {submitting || polling ? '生成中...' : '生成 TTS 音频'}
            </button>
          </div>
        </form>
      </div>

      {(result || activeTask) && (
        <div className={styles.resultCard}>
          <h2 className={styles.resultTitle}>生成结果</h2>
          <ul className={styles.resultList}>
            {activeTask && (
              <li>
                <span>任务状态：</span>
                <strong>{activeTask.status}</strong>
              </li>
            )}
            {typeof (result?.sentences_processed ?? activeTask?.sentences_processed) !== 'undefined' && (
              <li>
                <span>处理句子：</span>
                <strong>{result?.sentences_processed ?? activeTask?.sentences_processed}</strong>
              </li>
            )}
            <li>
              <span>使用发音人：</span>
              <strong>{result?.voice ?? activeTask?.voice ?? selectedVoice?.shortName}</strong>
            </li>
            {(result?.mode ?? activeTask?.mode) && (
              <li>
                <span>生成类型：</span>
                <strong>{result?.mode ?? activeTask?.mode}</strong>
              </li>
            )}
            {(result?.chapter_id ?? activeTask?.chapter_id) && (
              <li>
                <span>章节 ID：</span>
                <strong>{result?.chapter_id ?? activeTask?.chapter_id}</strong>
              </li>
            )}
            {typeof (result?.total_sentences ?? activeTask?.total_sentences) !== 'undefined' && (
              <li>
                <span>总句子数：</span>
                <strong>{result?.total_sentences ?? activeTask?.total_sentences}</strong>
              </li>
            )}
            {result?.characters && (
              <li>
                <span>字符数：</span>
                <strong>{result.characters}</strong>
              </li>
            )}
            {result?.audio_file && (
              <li>
                <span>音频文件：</span>
                <a
                  href={result.audio_file}
                  target="_blank"
                  rel="noreferrer"
                >
                  {result.audio_file}
                </a>
              </li>
            )}
            {result?.srt_file && (
              <li>
                <span>字幕文件：</span>
                <a
                  href={result.srt_file}
                  target="_blank"
                  rel="noreferrer"
                >
                  {result.srt_file}
                </a>
              </li>
            )}
          </ul>
          {/* ── 失败句子列表 ── */}
          {Array.isArray((result?.failed_sentences ?? activeTask?.failed_sentences)) && (result?.failed_sentences ?? activeTask?.failed_sentences).length > 0 && (
            <div className={styles.failedSection}>
              <h3 className={styles.failedTitle}>
                ⚠️ 失败句子（{(result?.failed_sentences ?? activeTask?.failed_sentences).length} 句）
              </h3>
              <p className={styles.failedHint}>
                已缓存成功的句子，下次运行时只需重新生成以下失败的句子。
              </p>
              <div className={styles.progressTableWrapper}>
                <table className={styles.progressTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>句子 ID</th>
                      <th>文本预览</th>
                      <th>错误信息</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result?.failed_sentences ?? activeTask?.failed_sentences).map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.index ?? '-'}</td>
                        <td>{item.sentence_id ?? '-'}</td>
                        <td title={item.text}>{item.text?.slice(0, 50) || ''}{item.text && item.text.length > 50 ? '…' : ''}</td>
                        <td className={styles.error} style={{ fontSize: '0.8rem' }}>{item.error?.slice(0, 80) || '未知错误'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {Array.isArray((result?.progress ?? activeTask?.progress)) && (result?.progress ?? activeTask?.progress).length > 0 && (
            <div className={styles.progressSection}>
              <button
                type="button"
                className={styles.progressToggle}
                onClick={() => setResultProgressExpanded(prev => !prev)}
              >
                {resultProgressExpanded ? '隐藏进度详情' : '显示进度详情'}
              </button>
              {resultProgressExpanded && (
                <div className={styles.progressTableWrapper}>
                  <table className={styles.progressTable}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>句子 ID</th>
                        <th>文本预览</th>
                        <th>耗时 (ms)</th>
                        <th>开始 (ms)</th>
                        <th>结束 (ms)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result?.progress ?? activeTask?.progress).map((item, index) => (
                        <tr key={index}>
                          <td>{item.index}</td>
                          <td>{item.sentence_id ?? '-'}</td>
                          <td title={item.text}>{item.text?.slice(0, 40) || ''}{item.text && item.text.length > 40 ? '…' : ''}</td>
                          <td>{item.duration_us ? Math.round(item.duration_us / 1000) : '-'}</td>
                          <td>{typeof item.start_ms === 'number' ? Math.round(item.start_ms) : '-'}</td>
                          <td>{typeof item.end_ms === 'number' ? Math.round(item.end_ms) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EdgettsManagement;
